import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Button,
  Tooltip,
  Switch,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Edit,
  Delete,
  Email,
  Phone,
  PowerSettingsNew,
  PersonAdd,
  PersonRemove,
  MoreVert,
  Visibility,
} from '@mui/icons-material';
import { Pagination } from '@mui/material';
import { Doctor } from '../../types';
import { getApiUrl } from '../../config/api';

interface DoctorsListProps {
  doctors: Doctor[];
  onEdit: (doctor?: Doctor) => void;
  onDelete: (id: number) => void;
  onStatusToggle: (id: number, isActive: boolean) => void;
  onView: (doctor: Doctor) => void;
  loading: boolean;
  page: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  search: string;
  onSearchChange: (search: string) => void;
  statusFilter: 'all' | 'active' | 'inactive' | 'approved' | 'pending';
  onStatusFilterChange: (filter: 'all' | 'active' | 'inactive' | 'approved' | 'pending') => void;
  selectedDoctors: number[];
  onSelectDoctor: (id: number) => void;
  onSelectAllDoctors: () => void;
  onBulkStatusUpdate: (isActive: boolean) => void;
}

const DoctorsList: React.FC<DoctorsListProps> = ({ 
  doctors, 
  onEdit, 
  onDelete, 
  onStatusToggle,
  onView,
  loading, 
  page, 
  totalPages, 
  totalItems, 
  onPageChange, 
  search, 
  onSearchChange, 
  statusFilter, 
  onStatusFilterChange,
  selectedDoctors,
  onSelectDoctor,
  onSelectAllDoctors,
  onBulkStatusUpdate
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedDoctorId, setSelectedDoctorId] = React.useState<number | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, doctorId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoctorId(doctorId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoctorId(null);
  };
  return (
    <Box>
      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search doctors"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name, email, or specialties"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => onStatusFilterChange(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {selectedDoctors.length > 0 && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {selectedDoctors.length} doctor(s) selected
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onBulkStatusUpdate(true)}
                  disabled={loading}
                  startIcon={<PersonAdd />}
                  sx={{
                    backgroundColor: '#00A76F',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#007867',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#9E9E9E',
                    }
                  }}
                >
                  Activate All
                </Button>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onBulkStatusUpdate(false)}
                  disabled={loading}
                  startIcon={<PersonRemove />}
                  sx={{
                    backgroundColor: '#FF5630',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#E53E3E',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#9E9E9E',
                    }
                  }}
                >
                  Deactivate All
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Doctors Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
              <TableCell padding="checkbox" sx={{ fontWeight: 600, color: 'text.primary' }}>
                <Checkbox
                  checked={selectedDoctors.length === doctors.length && doctors.length > 0}
                  indeterminate={selectedDoctors.length > 0 && selectedDoctors.length < doctors.length}
                  onChange={onSelectAllDoctors}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Specialties</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {doctors.map((doctor) => (
              <TableRow 
                key={doctor.id} 
                selected={selectedDoctors.includes(doctor.id)}
                sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedDoctors.includes(doctor.id)}
                    onChange={() => onSelectDoctor(doctor.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Avatar 
                      src={doctor.profileImage ? doctor.profileImage : undefined}
                      sx={{ mr: 2, bgcolor: '#00A76F', width: 40, height: 40 }}
                    >
                      {doctor.fullName.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {doctor.fullName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Email sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {doctor.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Phone sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {doctor.phoneNumber}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {doctor.specialization || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={doctor.isActive}
                      onChange={(e) => onStatusToggle(doctor.id, e.target.checked)}
                      color="success"
                      size="small"
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: doctor.isActive ? '#00A76F' : '#FF5630',
                        fontWeight: 500,
                        fontSize: '0.75rem'
                      }}
                    >
                      {doctor.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="View Doctor">
                      <IconButton
                        size="small"
                        onClick={() => onView(doctor)}
                        sx={{ 
                          color: '#00A76F',
                          '&:hover': {
                            backgroundColor: '#E8F5E9',
                            color: '#007867'
                          }
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Edit Doctor">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(doctor)}
                        sx={{ 
                          color: '#1976D2',
                          '&:hover': {
                            backgroundColor: '#E3F2FD',
                            color: '#0D47A1'
                          }
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="More Actions">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, doctor.id)}
                        sx={{ 
                          color: '#666',
                          '&:hover': {
                            backgroundColor: '#F5F5F5',
                            color: '#333'
                          }
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      {/* {totalPages > 1 && ( */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {((page - 1) * 10) + 1} to {Math.min(page * 10, totalItems)} of {totalItems} doctors
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
            size="large"
          />
        </Box>
      {/* )} */}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
          }
        }}
      >
        {selectedDoctorId && doctors.find(d => d.id === selectedDoctorId) && (
          <>
            <MenuItem
              onClick={() => {
                const doctor = doctors.find(d => d.id === selectedDoctorId);
                if (doctor) {
                  onView(doctor);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Doctor</ListItemText>
            </MenuItem>
            
            <MenuItem
              onClick={() => {
                const doctor = doctors.find(d => d.id === selectedDoctorId);
                if (doctor) {
                  onEdit(doctor);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Doctor</ListItemText>
            </MenuItem>
            
            <MenuItem
              onClick={() => {
                const doctor = doctors.find(d => d.id === selectedDoctorId);
                if (doctor) {
                  onStatusToggle(doctor.id, !doctor.isActive);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <PowerSettingsNew fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {doctors.find(d => d.id === selectedDoctorId)?.isActive ? 'Deactivate' : 'Activate'}
              </ListItemText>
            </MenuItem>
            
            <MenuItem
              onClick={() => {
                onDelete(selectedDoctorId);
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Delete Doctor</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default DoctorsList;
